import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { base44 } from "@/api/base44Client";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { X, FileText, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import TaskTemplateForm from "./TaskTemplateForm";

export default function TaskTemplateSelector({ onApplyTemplate, onClose }) {
  const [showTemplateForm, setShowTemplateForm] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const queryClient = useQueryClient();

  const { data: templates = [], isLoading } = useQuery({
    queryKey: ["taskTemplates"],
    queryFn: () => base44.entities.TaskTemplate.filter({ is_active: true }),
  });

  const deleteTemplateMutation = useMutation({
    mutationFn: (id) => base44.entities.TaskTemplate.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["taskTemplates"] });
      toast.success("Mal slettet");
    },
  });

  const handleApplyTemplate = (template) => {
    onApplyTemplate(template);
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-3xl max-h-[80vh] overflow-y-auto">
        <div className="p-6 border-b border-slate-100 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <FileText className="w-5 h-5 text-zinc-900" />
            <h2 className="text-xl font-semibold text-slate-900">Oppgavemaler</h2>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-lg transition-colors">
            <X className="w-5 h-5 text-slate-500" />
          </button>
        </div>

        <div className="p-6">
          <div className="flex justify-between items-center mb-4">
            <p className="text-sm text-slate-600">
              Velg en mal for å raskt opprette flere oppgaver
            </p>
            <Button
              onClick={() => setShowTemplateForm(true)}
              size="sm"
              className="bg-zinc-900 hover:bg-zinc-900"
            >
              <Plus className="w-4 h-4 mr-2" />
              Ny Mal
            </Button>
          </div>

          {isLoading ? (
            <div className="space-y-3">
              {[1, 2, 3].map(i => (
                <div key={i} className="h-24 bg-slate-100 rounded-lg animate-pulse" />
              ))}
            </div>
          ) : templates.length === 0 ? (
            <div className="text-center py-12">
              <FileText className="w-12 h-12 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500 mb-4">Ingen maler opprettet ennå</p>
              <Button
                onClick={() => setShowTemplateForm(true)}
                variant="outline"
              >
                <Plus className="w-4 h-4 mr-2" />
                Opprett første mal
              </Button>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-3">
              {templates.map((template) => (
                <Card
                  key={template.id}
                  className="p-4 hover:shadow-md transition-all cursor-pointer"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1" onClick={() => handleApplyTemplate(template)}>
                      <div className="flex items-center gap-2 mb-2">
                        <h3 className="font-semibold text-slate-900">{template.name}</h3>
                        {template.phase_name && (
                          <Badge variant="outline" className="text-xs">
                            {template.phase_name}
                          </Badge>
                        )}
                      </div>
                      {template.description && (
                        <p className="text-sm text-slate-600 mb-3">{template.description}</p>
                      )}
                      <div className="flex items-center gap-4 text-xs text-slate-500">
                        <span>{template.tasks?.length || 0} oppgaver</span>
                        <span>
                          {template.tasks?.reduce((sum, t) => sum + (t.estimated_hours || 0), 0) || 0}t estimert
                        </span>
                      </div>
                    </div>
                    <div className="flex gap-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (confirm("Er du sikker på at du vil slette denne malen?")) {
                            deleteTemplateMutation.mutate(template.id);
                          }
                        }}
                      >
                        <Trash2 className="w-4 h-4 text-slate-400 hover:text-rose-600" />
                      </Button>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </div>
      </Card>

      {showTemplateForm && (
        <TaskTemplateForm
          template={editingTemplate}
          onClose={() => {
            setShowTemplateForm(false);
            setEditingTemplate(null);
          }}
        />
      )}
    </div>
  );
}